import uniqueId from 'lodash/uniqueId';

export function createTextLayer(overrides = {}) {
    return {
        id: uniqueId('layer_'),
        type: 'text',
        name: 'Text',
        text: 'Text',
        font: 'Open Sans',
        fontSize: 48,
        color: 'rgba(255, 255, 255, 1)',
        imageSrc: '',
        x: 0.5,
        y: 0.5,
        scale: 1,
        rotation: 0,
        opacity: 100,
        visible: true,
        ...overrides,
    };
}

export function createImageLayer(imageSrc, overrides = {}) {
    return {
        id: uniqueId('layer_'),
        type: 'image',
        name: 'Image',
        text: '',
        font: 'Open Sans',
        fontSize: 48,
        color: 'rgba(255, 255, 255, 1)',
        imageSrc,
        x: 0.5,
        y: 0.5,
        scale: 1,
        rotation: 0,
        opacity: 100,
        visible: true,
        ...overrides,
    };
}

export function getEffectiveLayers(background, imageId, focusedImageId = null) {
    if (!background || !background.behindSubject) {
        return [];
    }

    const perImageEntry = background.perImage && background.perImage[imageId];
    if (perImageEntry && perImageEntry.useCustomLayers) {
        return perImageEntry.layers || [];
    }

    if (focusedImageId && imageId !== focusedImageId) {
        const focusedEntry = background.perImage && background.perImage[focusedImageId];
        if (focusedEntry && focusedEntry.useCustomLayers) {
            return background.layers || [];
        }
    }

    return background.layers || [];
}

export function hasBehindLayers(background, imageId, focusedImageId = null) {
    return getEffectiveLayers(background, imageId, focusedImageId).length > 0;
}

export function normalizedToPixelPosition(layer, width, height) {
    return {
        x: (layer.x || 0) * width,
        y: (layer.y || 0) * height,
    };
}

export function pixelToNormalizedPosition(x, y, width, height) {
    if (!width || !height) {
        return { x: 0.5, y: 0.5 };
    }

    return {
        x: x / width,
        y: y / height,
    };
}

const imageCache = new Map();

function loadImage(src) {
    if (!src) {
        return Promise.resolve(null);
    }

    if (imageCache.has(src)) {
        return Promise.resolve(imageCache.get(src));
    }

    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            imageCache.set(src, image);
            resolve(image);
        };
        image.onerror = () => reject(new Error('Failed to load behind layer image'));
        image.src = src;
    });
}

export function getLayerTextFontSize(layer, width) {
    return Math.max(1, ((layer.fontSize || 48) / 100) * width);
}

export function drawTextLayerPreview(canvas, layer, displayWidth, opacity = 1) {
    const fontSize = getLayerTextFontSize(layer, displayWidth);
    const text = layer.text || '';
    const font = `${fontSize}px ${layer.font || 'Open Sans'}`;
    const ctx = canvas.getContext('2d');

    ctx.font = font;
    const metrics = ctx.measureText(text);
    canvas.width = Math.max(1, Math.ceil(metrics.width));
    canvas.height = Math.max(1, Math.ceil(fontSize));

    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = layer.color || 'rgba(255, 255, 255, 1)';
    ctx.globalAlpha = opacity;
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
}

export async function drawBehindLayers(ctx, layers, { w, h }) {
    for (const layer of layers) {
        if (!layer.visible) {
            continue;
        }

        const { x, y } = normalizedToPixelPosition(layer, w, h);
        const opacity = (layer.opacity != null ? layer.opacity : 100) / 100;
        const rotation = ((layer.rotation || 0) * Math.PI) / 180;

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(-rotation);
        ctx.globalAlpha = opacity;

        if (layer.type === 'text') {
            const fontSize = getLayerTextFontSize(layer, w);
            ctx.font = `${fontSize}px ${layer.font || 'Open Sans'}`;
            ctx.fillStyle = layer.color || 'rgba(0, 0, 0, 0.85)';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(layer.text || '', 0, 0);
        } else if (layer.type === 'image' && layer.imageSrc) {
            const image = await loadImage(layer.imageSrc);
            if (image) {
                const scale = layer.scale != null ? layer.scale : 1;
                const drawWidth = w * scale;
                const aspect = image.height / image.width;
                const drawHeight = drawWidth * aspect;
                ctx.drawImage(image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
            }
        }

        ctx.restore();
    }
}
